// src/components/tour_groups_calendar/content/TourGroupCardItem.tsx
import styled from "styled-components";
import { useState } from "react";
import { Avatar, Chip } from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TourGroupModal from "../../bookings_manager/tour_guides/content/external_assignments/TourGroupModal";

//------------------------------------------------------------------------

const Card = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProductTitle = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
  line-height: 1.3;
`;

const OptionTitle = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #555;

  svg {
    font-size: 1.1rem;
    color: #666;
  }
`;

const AssignmentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid #eee;
`;

const AssignmentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
`;

const StaffAvatar = styled(Avatar)`
  width: 24px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
`;

const NotesSection = styled.div`
  padding: 8px;
  background-color: #fffbea;
  border-left: 3px solid #ffca28;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #666;
`;

//------------------------------------------------------------------------

interface TourGroupCardItemProps {
  tourGroup: any;
  entities: any;
  refetch: () => void;
}

const TourGroupCardItem = ({
  tourGroup,
  entities,
  refetch,
}: TourGroupCardItemProps) => {
  const [isTourGroupModalShown, setShowTourGroupModal] = useState(false);

  const product = entities?.products?.find(
    (p: any) => p._id === tourGroup.product_id
  );
  const option = product?.options?.find(
    (o: any) => o._id === tourGroup.option_id
  );
  const startTime = product?.start_times?.find(
    (st: any) => st._id === tourGroup.start_time_id
  );
  const vehicle = entities?.vehicles?.find(
    (v: any) => v._id === tourGroup.vehicle_id
  );

  // Get assigned staff from external_assignments
  const externalAssignments = tourGroup.external_assignments || [];

  // Get schedule notes
  const scheduleNotes = tourGroup.schedule_notes_list || [];
  const hasNotes = scheduleNotes.length > 0;

  return (
    <>
      <Card onClick={() => setShowTourGroupModal(true)}>
      <CardHeader>
        <ProductTitle>
          {product?.title || `Product ${tourGroup.product_id}`}
        </ProductTitle>
        <OptionTitle>{option?.title || "N/A"}</OptionTitle>
      </CardHeader>

      <InfoRow>
        <AccessTimeIcon />
        <span>{startTime?.time_slot || "N/A"}</span>
      </InfoRow>

      {vehicle && (
        <InfoRow>
          <DirectionsBusIcon />
          <span>
            {vehicle.plate}
            {tourGroup.is_projected_vehicle && (
              <Chip
                label="Projected"
                size="small"
                sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
              />
            )}
          </span>
        </InfoRow>
      )}

      <InfoRow>
        <GroupIcon />
        <span>{tourGroup.seatsCount || 0} seats</span>
      </InfoRow>

      {externalAssignments.length > 0 && (
        <AssignmentsSection>
          {externalAssignments.map((assignment: any, index: number) => {
            const user = entities?.staff?.find(
              (u: any) => u._id === assignment.user_id
            );
            const role = entities?.roles?.find(
              (r: any) => r._id === assignment.role_id
            );
            return (
              <AssignmentRow key={index}>
                <StaffAvatar>
                  {user?.name ? user.name.charAt(0).toUpperCase() : "?"}
                </StaffAvatar>
                <span>{user?.name || "Unknown"}</span>
                {role && (
                  <Chip label={role.title} size="small" sx={{ height: 20 }} />
                )}
              </AssignmentRow>
            );
          })}
        </AssignmentsSection>
      )}

      {hasNotes && (
        <NotesSection>
          <strong>Notes:</strong>{" "}
          {scheduleNotes.map((note: any, idx: number) => (
            <div key={idx}>• {note.body}</div>
          ))}
        </NotesSection>
      )}
    </Card>

    {isTourGroupModalShown && (
      <TourGroupModal
        close={() => {
          setShowTourGroupModal(false);
        }}
        id={tourGroup._id}
      />
    )}
    </>
  );
};

export default TourGroupCardItem;
